import {IGetOrderResponse, IOrderDetails} from "@/types/order";
import {IOrderGetItems} from "@/types/order";
import Factor from "@/types/factor";
import {apiEndpoints} from "@/utils/api-endpoints";
import {api} from "@/redux/api.slice";
import PageResponse from "@/types/page-response";

const orderApi = api.injectEndpoints({
    overrideExisting: true,

    endpoints: (builder) => ({
        getOrders: builder.query<PageResponse<IOrderDetails[]>, { normalOrders?: boolean; page?: number }>({
            query: ({normalOrders = true, page = 1}) => {
                const params = new URLSearchParams();
                params.append("normalOrders", String(normalOrders));
                params.append("page", String(page));

                return {
                    url: apiEndpoints.Order.GetOrders.url(params.toString()),
                    method: apiEndpoints.Order.GetOrders.method,
                };
            },
            providesTags: ["Order"],
        }),
        getOrdersAdmin: builder.query<
            PageResponse<IOrderDetails[]>,
            { normalOrders?: boolean; page?: number; searchText?: string }
        >({
            query: ({normalOrders = true, page = 1, searchText = ""}) => {
                const params = new URLSearchParams();
                params.append("normalOrders", String(normalOrders));
                params.append("page", String(page));
                if (searchText) {
                    params.append("searchText", searchText);
                }

                return {
                    url: apiEndpoints.Order.GetAdminOrders.url(params.toString()),
                    method: apiEndpoints.Order.GetAdminOrders.method,
                };
            },
            providesTags: ["Order"],
        }),
        getUserOrders: builder.query<
            PageResponse<IOrderDetails[]>,
            { userId: string; normalOrders?: boolean; page?: number }
        >({
            query: ({ userId, normalOrders = true, page = 1 }) => {
                const params = new URLSearchParams();
                params.append("normalOrders", String(normalOrders));
                params.append("page", String(page));

                return {
                    url: apiEndpoints.Order.GetUserOrders.url(userId, params.toString()),
                    method: apiEndpoints.Order.GetUserOrders.method,
                };
            },
            providesTags: ["Order"],
        }),
        getOrder: builder.query<IGetOrderResponse, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.GetOrder.url(orderId.toString()),
                method: apiEndpoints.Order.GetOrder.method,
            }),
            providesTags: ["Order"],
        }),
        getOrderAdmin: builder.query<IGetOrderResponse, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.GetOrderAdmin.url(orderId.toString()),
                method: apiEndpoints.Order.GetOrderAdmin.method,
            }),
            providesTags: ["Order"],
        }),


        getItems: builder.query<IOrderGetItems[], number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.GetItems.url(orderId.toString()),
                method: apiEndpoints.Order.GetItems.method,
            }),
            providesTags: ["Order"],
        }),

        confirmOrder: builder.mutation<void, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.Confirm.url(orderId.toString()),
                method: apiEndpoints.Order.Confirm.method,
            }),
            invalidatesTags: ["Order"],
        }),

        deliverOrder: builder.mutation<void, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.Deliver.url(orderId.toString()),
                method: apiEndpoints.Order.Deliver.method,
            }),
            invalidatesTags: ["Order"],
        }),

        rejectOrder: builder.mutation<void, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.Reject.url(orderId.toString()),
                method: apiEndpoints.Order.Reject.method,
            }),
            invalidatesTags: ["Order"],
        }),

        getFactor: builder.query<Factor, number>({
            query: (orderId) => ({
                url: apiEndpoints.FactorCheck.GetFactorPrint.url(orderId.toString()),
                method: apiEndpoints.FactorCheck.GetFactorPrint.method,
            }),
            providesTags: ["Order"],
        }),
        getOrderTransport: builder.query<any, number>({
            query: (orderId) => ({
                url: apiEndpoints.Order.GetOrdersTransport.url(orderId.toString()),
                method: apiEndpoints.Order.GetOrdersTransport.method,
            }),
            providesTags: ["Order"],
        }),
    }),
});

export const {
    useGetOrdersQuery,
    useGetOrdersAdminQuery,
    useGetUserOrdersQuery,
    useLazyGetOrderQuery,
    useLazyGetOrderAdminQuery,
    useLazyGetItemsQuery,
    useConfirmOrderMutation,
    useDeliverOrderMutation,
    useRejectOrderMutation,
    useGetFactorQuery,
    useGetOrderTransportQuery,
} = orderApi;